import {ValidationErrors} from '@angular/forms';
import * as moment from 'moment';
import {Moment} from 'moment';
import {MaskElementDirective} from '../../../interfaces/mask';
import {DateMaskFunction} from '../../enums/custom-mask-regex';
import {MaskValidatorReturn} from '../../interfaces/mask-validator-return';
import {dayInMonths} from '../../static/staticValues';

export class MaskDate {
  constructor() {}

  private functionDates: {[key: string]: Function} = {
    D: this.dayCheck.bind(this),
    M: this.monthCheck.bind(this),
    Y: this.yearCheck.bind(this),
    _: this.noCheck.bind(this),
  };

  getControls(
    ev: string,
    arrMasks: MaskElementDirective,
    dateLimits: {
      min: Moment;
      max: Moment;
    },
    deletingTxt = '',
  ): MaskValidatorReturn {
    const returnObj: MaskValidatorReturn = {
      error: null,
      text: null,
      mask: null,
    };

    const isDeleting = deletingTxt.length > 0;
    const baseTxt = isDeleting ? deletingTxt : ev;
    const splitted = baseTxt.split(/(\W+)/).filter((el) => el.length > 0);

    const objValues: any = {};
    if (splitted.length > arrMasks.mask.length) {
      return {
        error: null,
        text: splitted.slice(0, splitted.length - arrMasks.mask.length).join(''),
        mask: null,
      };
    }

    splitted.forEach((el: string, i: number) => {
      const char = <any>(arrMasks.mask[i] as string)[0];
      const typeName = DateMaskFunction[char] || DateMaskFunction.NO_FN;
      objValues[typeName] = {val: el, mask: arrMasks.mask[i]};
    });

    const replaceDate: string[] = [];
    const lastEl = splitted.length - 1;
    for (const i in splitted) {
      const val = splitted[i];
      const maskEl = <string>arrMasks.mask[i];
      const typeName = DateMaskFunction[<any>maskEl[0]] || DateMaskFunction.NO_FN;

      if (isDeleting && i === lastEl.toString()) {
        const isNofn = typeName === DateMaskFunction.NO_FN;
        const txt = isNofn ? baseTxt.slice(0, -1 * (1 + maskEl.length)) : ev;
        return {
          error: null,
          text: txt,
          mask: arrMasks.maskText.slice(txt.length),
        };
      }

      const {text, error, mask} = this.functionDates[typeName](splitted, arrMasks.mask, i, dateLimits);

      if (error) {
        returnObj.error = returnObj.error || error;

        break;
      }
      replaceDate.push(text);
      if (val.length !== maskEl.length) {
        break;
      }
    }
    returnObj.text = replaceDate.join('');
    const maskText = arrMasks.maskText;
    returnObj.mask = maskText.slice(returnObj.text.length);

    return returnObj;
  }

  private checkMax(number: number, max: number, valLength: number, val: string): MaskValidatorReturn | null {
    if (number > max || (number === 0 && valLength > 1)) {
      return {
        error: null,
        text: val.slice(0, -1),
        mask: null,
      };
    }
    return null;
  }

  private addZeros(number: number, max: number, valLength: number, val: string, maskLength: number): MaskValidatorReturn | null {
    if (maskLength > 1 && number > max && valLength < maskLength) {
      const zeros: string = new Array(maskLength - valLength).fill(0).join('');
      return {
        error: null,
        text: zeros + val,
        mask: null,
      };
    }

    return null;
  }

  private wellWritten(number: number, max: number, val: string, maskLength: number, nextPart: string | null): MaskValidatorReturn | null {
    const valLength = val.length;

    if (valLength > 0 && valLength === maskLength && number <= max) {
      return {
        error: null,
        text: val + (nextPart || ''),
        mask: null,
      };
    }
    return null;
  }

  private isNumber(val: string): MaskValidatorReturn | number {
    /**
     * if isNan() not works
     * there  this strange rule because parseInt("1a") is converted
     *  to 1, but it needs also 09 as two, so i need to check that every
     * chars is a number
     */
    // const isInt = val.split('').reduce((check: boolean, el: string) => {
    //   return check && parseInt(el) >= 0;
    // }, true);

    return isNaN(<any>val)
      ? {
          error: null,
          text: val.slice(0, -1),
          mask: null,
        }
      : parseInt(val);
  }

  private dayCheck(
    splitted: string[],
    maskArr: string[],
    index: number,
    _dateLimits: {min: Moment; max: Moment},
  ): MaskValidatorReturn | null {
    const val = splitted[index];
    const mask = maskArr[index];
    const maskLength = mask.length;
    const valLength = val.length;
    const d = this.isNumber(val);

    if (typeof d !== 'number') {
      return d;
    }

    const checkMaxD = this.checkMax(d, 31, valLength, val);
    if (checkMaxD) {
      return checkMaxD;
    }
    const addZeros = this.addZeros(d, 3, valLength, val, maskLength);
    if (addZeros) {
      return addZeros;
    }

    if (maskLength > 1 && d > 3 && valLength < maskLength) {
      const zeros: string = new Array(maskLength - valLength).fill(0).join('');
      return {
        error: null,
        text: zeros + d.toString(),
        mask: null,
      };
    }
    const wellWritten = this.wellWritten(
      d,
      31,
      val,
      maskLength,
      (splitted.length - 1 === parseInt(index.toString()) && maskArr[parseInt(index.toString()) + 1]) || null,
    );
    if (wellWritten) {
      return wellWritten;
    }

    return {
      error: null,
      text: d.toString(),
      mask: null,
    };
  }

  private monthCheck(splitted: string[], maskArr: string[], index: number, _dateLimits: {min: Moment; max: Moment}) {
    const val = splitted[index];
    const mask = maskArr[index];
    const maskLength = mask.length;
    const valLength = val.length;

    const month = this.isNumber(val);
    if (typeof month !== 'number') {
      return month;
    }

    const checkMaxD = this.checkMax(month, 12, valLength, val);
    if (checkMaxD) {
      return checkMaxD;
    }

    const addZeros = this.addZeros(month, 1, valLength, val, maskLength);
    if (addZeros) {
      return addZeros;
    }

    const dayIndex = maskArr.findIndex((el) => {
      return el[0] === DateMaskFunction.D.toString();
    });

    if (dayIndex && splitted[dayIndex]?.length > 0) {
      const day = parseInt(splitted[dayIndex]);
      const monthsWithdays = dayInMonths.find((el, i) => {
        return day <= el && i === month - 1;
      });
      if (!monthsWithdays) {
        return {
          error: {invalidDate: true},
          text: val,
          mask: null,
        };
      }
    }

    const wellWritten = this.wellWritten(
      month,
      12,
      val,
      maskLength,
      (splitted.length - 1 === parseInt(index.toString()) && maskArr[parseInt(index.toString()) + 1]) || null,
    );
    if (wellWritten) {
      return wellWritten;
    }

    return {
      error: null,
      text: val,
      mask: null,
    };
  }
  private yearCheck(splitted: string[], maskArr: string[], index: number, dateLimits: {min: Moment; max: Moment}) {
    const val = splitted[index];
    const mask = maskArr[index];
    const maskLength = mask.length;

    const valLength = val.length;
    const limitsYears = {
      min: 0,
      max: 0,
    };

    const year = this.isNumber(val);
    if (typeof year !== 'number') {
      return year;
    }

    if (valLength > maskLength) {
      return {
        error: null,
        text: val.slice(0, maskLength),
        mask: null,
      };
    }
    if (dateLimits) {
      (Object.keys(dateLimits) as ('min' | 'max')[]).forEach((el: 'min' | 'max') => {
        if (moment.isMoment(dateLimits[el])) {
          limitsYears[el] = parseInt(dateLimits[el].format(mask)) || 0;
        }
      });
    }
    const rangeMinDate = parseInt(val + new Array(maskLength - valLength).fill(0).join(''));
    const rangeMaxDate = parseInt(val + new Array(maskLength - valLength).fill(9).join(''));

    if (limitsYears.max > 0) {
      const checkMaxD = this.checkMax(year, limitsYears.max, valLength, val);
      if (checkMaxD) {
        return checkMaxD;
      }
    }

    if (valLength > 0 && limitsYears.min > 0 && rangeMaxDate < limitsYears.min) {
      return {
        error: null,
        text: val.slice(0, -1),
        mask: null,
      };
    }
    const wellWritten = this.wellWritten(
      year,
      limitsYears.max,
      val,
      maskLength,
      (splitted.length - 1 === parseInt(index.toString()) && maskArr[parseInt(index.toString()) + 1]) || null,
    );
    if (wellWritten) {
      return wellWritten;
    }

    return {
      error: null,
      text: val,
      mask: null,
    };
  }

  private noCheck(splitted: string[], maskArr: string[], index: number, _dateLimits: {min: Moment; max: Moment}) {
    const mask = maskArr[index];
    return {
      error: null,
      text: mask.slice(0, mask.length),
      mask: null,
    };
  }
}
